import React, { FunctionComponent, useEffect, useState } from 'react'
import { t } from '@lingui/macro'
import { useParams } from 'react-router-dom'
import { UsersImportStep } from '../types/UserImportStep'
import { StepIndicator } from '../components/step-indicator/StepIndicator'
import { FieldsStepComponent } from './steps/fields-mapping/FieldsStepComponent'
import { ResultsStepComponent } from './steps/results/ResultsStepComponent'
import { UsersImportStatusEnum } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import NotFound from '../../../components/common/not-found/NotFound'
import { UsersCsvImportModelFragment } from '../operations/UsersImportFragments.codegen'
import { useFetchUsersImportDataQuery } from './UsersImportDetails.codegen'
import { SettingsStepComponent } from './steps/settings/SettingsStepComponent'
import { FullBlockSpinner } from 'front-end/src/components'

interface IRouteParams {
  importId: string
}

const STEP_COMPONENTS_MAP = {
  [UsersImportStep.FIELDS]: FieldsStepComponent,
  [UsersImportStep.SETTINGS]: SettingsStepComponent,
  [UsersImportStep.RESULTS]: ResultsStepComponent
}

const selectStepFor = (
  usersImport?: UsersCsvImportModelFragment | null
): UsersImportStep => {
  if (!usersImport || !usersImport.importMapping) {
    return UsersImportStep.FIELDS
  }

  if (usersImport.status === UsersImportStatusEnum.CREATED) {
    return UsersImportStep.SETTINGS
  }

  return UsersImportStep.RESULTS
}

const PageWrapper: FunctionComponent = ({ children }) => (
  <div className="UsersImportDetailsPage">{children}</div>
)

export const useUsersImportDetailsQueryWithParams = () => {
  const { importId } = useParams<IRouteParams>()

  return useFetchUsersImportDataQuery({
    fetchPolicy: 'network-only',
    variables: { id: parseInt(importId, 10) }
  })
}

export const UsersImportDetails: FunctionComponent = () => {
  const [currentStep, setStep] = useState(UsersImportStep.DATA)
  const { loading, data } = useUsersImportDetailsQueryWithParams()

  const StepComponent = STEP_COMPONENTS_MAP[currentStep]
  const usersImport = data?.admin?.usersImport?.import

  useEffect(() => {
    const newStep = selectStepFor(usersImport)
    setStep(newStep)
  }, [usersImport, setStep])

  if (loading) {
    return <FullBlockSpinner />
  }

  if (!usersImport) {
    return (
      <PageWrapper>
        <NotFound message={t`Bulk import not found`} />
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      {currentStep < UsersImportStep.RESULTS && (
        <StepIndicator loading={loading} currentStep={currentStep} />
      )}
      <StepComponent usersImport={usersImport} setStep={setStep} />
    </PageWrapper>
  )
}
